/* src/pages/RestaurantITServices.js */

import React from "react";
import PageSection from "../components/PageSection";
import { Helmet } from "react-helmet-async";
import styles from "./Pages.module.css";
import { Link } from "react-router-dom";

function RestaurantITServices() {
  const faqs = [
    {
      question: "Can you help if our POS goes down during service?",
      answer:
        "I can often help remotely in real time for network-related POS issues. I handle the IT side, network connectivity, hardware, and configuration. For POS software bugs, you'd contact your POS vendor. But most POS failures during service are network problems, not software bugs, and those I can usually diagnose and fix fast.",
    },
    {
      question: "I use Toast / Square / Clover. Do you work with those?",
      answer:
        "I handle the IT infrastructure those systems run on, your network, your hardware, and your internet connection. I'm not POS software support, but I make sure the IT side is solid so your POS works reliably. When something breaks, I can help you figure out whether it's an IT problem or a POS software problem.",
    },
    {
      question: "Can you set up separate Wi-Fi for customers and staff?",
      answer:
        "Yes. Separating customer Wi-Fi from your staff and POS network is a smart move, it keeps your business systems protected and ensures customer traffic doesn't slow down your POS or kitchen displays. I handle the setup and configuration.",
    },
    {
      question: "How quickly can you respond when something breaks?",
      answer:
        "Members get priority scheduling for remote sessions. Most network and hardware issues can be diagnosed remotely. When something needs hands-on attention, I serve Shreveport and Bossier City with on-site visits. I work around your schedule and try to respond as fast as my availability allows.",
    },
  ];

  return (
    <PageSection title="IT Support for Restaurants & Food Service in Shreveport-Bossier" cta faqs={faqs}>
      <Helmet>
        <title>IT Support for Restaurants in Shreveport-Bossier | Tharpe Industries</title>
        <meta
          name="description"
          content="IT support for restaurants and food service businesses in Shreveport and Bossier City. Keep your POS systems, Wi-Fi, and back-office computers running during service hours."
        />
        <meta
          name="keywords"
          content="IT support restaurants Shreveport, POS system support Bossier City, restaurant computer repair, food service IT support Louisiana"
        />
        <link rel="canonical" href="https://tharpeindustries.com/it-support-restaurants" />
      </Helmet>

      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <h2>Keep your POS, Wi-Fi, and back-office systems running during service hours.</h2>
          <div className={styles.heroQuickCta}>
            <Link to="/it-health-check" className={styles.heroQuickCtaBtn}>Book an IT Health Check, $149 →</Link>
          </div>
          <p>
            When you're running a restaurant, every minute of downtime during service costs you money. A POS that won't process cards, Wi-Fi that drops during a busy Friday night, a printer that won't talk to the kitchen, these aren't just inconveniences. They're lost revenue, frustrated customers, and chaos during your busiest hours.
          </p>
          <p>
            Austin Tharpe helps Shreveport-Bossier restaurants and food service businesses keep their technology running reliably. I handle POS connectivity, back-office computers, staff Wi-Fi, and the IT basics that keep your operation smooth.
          </p>

          <h3>Why restaurants have unique IT challenges</h3>
          <ul>
            <li><strong>POS downtime during service is a crisis:</strong> When your point-of-sale system goes down at 7 PM on a Saturday, you can't process cards, track orders, or split checks. Every minute it's down costs you real money. IT problems at the worst possible moment are what I help prevent.</li>
            <li><strong>Wi-Fi that handles staff and customers:</strong> Staff devices, order tablets, kitchen displays, and customer Wi-Fi all running on the same connection, without the right setup, they fight each other and everything slows down during peak hours.</li>
            <li><strong>Back-office computers do more than you think:</strong> Scheduling, payroll, vendor ordering, inventory tracking, your back-office computer is the nerve center of your operation. When it's slow, outdated, or infected with malware, your management team can't work efficiently.</li>
            <li><strong>No IT department on call:</strong> When something breaks at 5:45 PM before a dinner rush, you don't have a tech person to call. Most break/fix companies can't get to you in time. Having a local IT contact who knows your setup is the difference between a quick fix and a disaster.</li>
          </ul>

          <h3>What Tharpe Industries handles for restaurants</h3>
          <ul>
            <li><strong>POS network connectivity:</strong> POS systems depend on your network. Slow connections, dropped Wi-Fi, or network conflicts between devices can cause POS failures. I handle network diagnostics, configuration, and optimization so your POS stays up during service.</li>
            <li><strong>Wi-Fi setup and optimization:</strong> Staff tablets, kitchen displays, customer Wi-Fi, and back-office computers all need reliable, separated network access. We design and configure your Wi-Fi to handle the load without slowing down during busy periods.</li>
            <li><strong>Back-office computer maintenance:</strong> Slow computers, outdated software, malware, failing hard drives, I handle diagnostics, repairs, and upgrades for your back-office systems so your managers and admin staff can work without frustration.</li>
            <li><strong>Backup for business records:</strong> Vendor contacts, financial records, employee files, and operational data need to be backed up automatically. A hard drive failure shouldn't mean losing your entire business history.</li>
            <li><strong>Printer and peripheral support:</strong> Receipt printers, kitchen ticket printers, cash drawers, when these stop working, service stops. I handle setup, troubleshooting, and replacement coordination.</li>
          </ul>

          <h3>POS software support: I handle the IT, you handle the software</h3>
          <p>
            I handle the IT infrastructure your POS runs on, network connectivity, hardware performance, and network security. For POS software-specific issues (Toast, Square, Clover, Aloha, etc.), you'll work with your POS vendor's support line. But most POS problems are actually network or hardware problems, and that's what I fix.
          </p>

          <div className={styles.ctaContainer}>
            <p><strong>Not sure if your POS setup can handle your busiest nights?</strong></p>
            <p className={styles.emergencyText}>The $149 IT Health Check covers your network, back-office computers, and backup setup, and gives you a clear picture of what could fail during service hours before it actually does.</p>
            <Link to="/it-health-check" className={styles.ctaButton}>Start with the IT Health Check</Link>
          </div>

          <div className={styles.secondaryCta}>
            <p>Questions about your restaurant's setup? <Link to="/contact-us">Send me a message</Link> and I'll help you think it through.</p>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '0.75rem' }}>I also serve other local businesses:</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem' }}>
              <li><Link to="/it-support-medical-offices" style={{ fontSize: '0.9rem' }}>Medical Offices</Link></li>
              <li><Link to="/it-support-dental-offices" style={{ fontSize: '0.9rem' }}>Dental Practices</Link></li>
              <li><Link to="/it-support-law-firms" style={{ fontSize: '0.9rem' }}>Law Firms</Link></li>
              <li><Link to="/it-support-accounting-firms" style={{ fontSize: '0.9rem' }}>Accounting Firms</Link></li>
              <li><Link to="/it-support-contractors" style={{ fontSize: '0.9rem' }}>Contractors & Trades</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </PageSection>
  );
}

export default RestaurantITServices;
